import type { LeanCustomsDeclaration } from "../services/leancustomsService";
import {
  leanCustomsDeclarationLinkedOrderId,
  leanCustomsDeclarationMatchesBooking,
} from "./bookingUtils";

export function declarationBelongsToOrder(
  declaration: Record<string, unknown>,
  orderId: string
): boolean {
  const linked = leanCustomsDeclarationLinkedOrderId(declaration);
  return linked != null && linked === orderId;
}

/**
 * Search `q` values for Lean Customs declaration/order list APIs.
 * Composite refs like "618-57013611 / EFF7323AIR" are split so Dexwork can match
 * either the AWB/container part or the EFF token alone.
 */
export function buildLeanCustomsDeclarationSearchTerms(
  bookingReference: string,
  bookingId: number,
  extraTerms: string[] = []
): string[] {
  const terms: string[] = [];
  const push = (raw: string | null | undefined) => {
    const s = typeof raw === "string" ? raw.trim() : "";
    if (!s) return;
    if (!terms.includes(s)) terms.push(s);
  };

  push(bookingReference);
  push(`EFF${bookingId}`);

  for (const part of bookingReference.split(/\s*\/\s*/)) {
    push(part);
  }

  const effToken = bookingReference.match(/EFF\d+[A-Za-z]*/i);
  if (effToken) push(effToken[0].toUpperCase());

  for (const extra of extraTerms) {
    push(extra);
    for (const part of extra.split(/\s*\/\s*/)) {
      push(part);
    }
  }

  return terms;
}

/** Pull declaration UUIDs out of order / shipment payloads from Dexwork. */
export function collectDeclarationIdsFromLeanPayload(payload: unknown): string[] {
  const ids = new Set<string>();

  const pushId = (value: unknown) => {
    if (typeof value !== "string") return;
    const id = value.trim();
    if (
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
    ) {
      ids.add(id);
    }
  };

  const visit = (node: unknown, depth: number) => {
    if (node == null || depth > 8) return;
    if (Array.isArray(node)) {
      for (const item of node) visit(item, depth + 1);
      return;
    }
    if (typeof node !== "object") return;
    const rec = node as Record<string, unknown>;

    pushId(rec.declaration_id);
    pushId(rec.declarationId);
    if (rec.declaration && typeof rec.declaration === "object") {
      pushId((rec.declaration as { id?: unknown }).id);
    }
    // Declaration records themselves (id + reference/mrn shape)
    if (
      typeof rec.id === "string" &&
      (typeof rec.mrn === "string" ||
        typeof rec.lrn === "string" ||
        typeof rec.dms_status === "string" ||
        typeof rec.declaration_type === "string")
    ) {
      pushId(rec.id);
    }

    for (const key of [
      "declarations",
      "declaration",
      "shipments",
      "results",
      "data",
      "items",
    ] as const) {
      if (key in rec) visit(rec[key], depth + 1);
    }
  };

  visit(payload, 0);
  return [...ids];
}

/** Extra list-search terms derived from a linked Lean Customs order record. */
export function searchTermsFromLeanCustomsOrder(
  order: Record<string, unknown> | null | undefined
): string[] {
  if (!order) return [];
  const terms: string[] = [];
  const push = (raw: unknown) => {
    if (typeof raw === "string" && raw.trim()) terms.push(raw.trim());
    if (typeof raw === "number" && Number.isFinite(raw)) terms.push(String(raw));
  };

  push(order.reference);
  push(order.booking_number);
  push(order.booking_reference);
  const ticket = order.ticket_number;
  if (ticket != null && String(ticket).trim()) {
    terms.push(String(ticket).trim());
    terms.push(`LC-${String(ticket).trim()}`);
  }

  return terms;
}

export function mergeDeclarationsById(
  lists: LeanCustomsDeclaration[][]
): LeanCustomsDeclaration[] {
  const map = new Map<string, LeanCustomsDeclaration>();
  for (const list of lists) {
    for (const d of list) {
      if (d.id) map.set(d.id, d);
    }
  }
  return [...map.values()];
}

/** Declarations matching this booking reference (no order filter). */
export function pickDeclarationsForBooking(
  candidates: LeanCustomsDeclaration[],
  bookingId: number
): LeanCustomsDeclaration[] {
  return mergeDeclarationsById([
    candidates.filter((d) => leanCustomsDeclarationMatchesBooking(d, bookingId)),
  ]);
}

/** Prefer declarations linked to the order; fall back to booking reference match. */
export function pickDeclarationsForLinkedOrder(
  candidates: LeanCustomsDeclaration[],
  orderId: string,
  bookingId: number
): LeanCustomsDeclaration[] {
  const onOrder = candidates.filter((d) => declarationBelongsToOrder(d, orderId));
  if (onOrder.length > 0) return mergeDeclarationsById([onOrder]);

  return mergeDeclarationsById([
    candidates.filter((d) => leanCustomsDeclarationMatchesBooking(d, bookingId)),
  ]);
}
